import { BookOpenCheckIcon, CalendarCheckIcon, FileTextIcon, FolderOpenIcon, ListChecksIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type BonusGroup = {
  heading: string;
  items: string[];
};

export type Bonus = {
  number: string;
  title: string;
  description: string;
  items?: string[];
  groups?: BonusGroup[];
  cycle?: string[];
  benefit: string;
  value: string;
  icon: LucideIcon;
  wide?: boolean;
};

export const bonuses: Bonus[] = [
  {
    number: '01',
    title: '150 Mineral Processing Classroom Activities & Technical Exercises',
    description:
      '150 ready-to-use activity ideas covering mineral processing fundamentals, comminution and size reduction, screening and classification, gravity and magnetic separation, froth flotation, dewatering and filtration, plant operations, safety and environmental responsibility, troubleshooting, technical communication, critical thinking, and integrated mineral processing situations. Organized by difficulty level and topic.',
    benefit: 'Materials already designed for your classroom: saves hours every quarter.',
    value: 'USD 14',
    icon: FolderOpenIcon,
  },
  {
    number: '02',
    title: 'Mineral Processing Teacher Assessment Kit',
    description:
      'Comprehensive assessment resources designed to evaluate student learning throughout a mineral processing course. Includes:',
    items: [
      'Foundational level assessments',
      'Intermediate level assessments',
      'Advanced level assessments',
      'Concept-understanding questions',
      'Technical reasoning questions',
      'Scenario-based assessments',
      'Application-focused questions',
      'Equipment and process assessments',
      'Circuit design assessments',
      'Plant operations assessments',
      'Safety and environmental assessments',
      'Troubleshooting assessments',
      'Calculation-based activities',
      'Integrated processing assessments',
      'Teacher guidance',
    ],
    benefit: 'Never build an exam from scratch under pressure again.',
    value: 'USD 14',
    icon: ListChecksIcon,
  },
  {
    number: '03',
    title: '100 Mineral Processing Industrial Case Studies & Problem-Solving Scenarios',
    description:
      'A collection of 100 developed mineral processing cases involving crushing and grinding, screening and classification, gravity and magnetic separation, flotation circuits, dewatering, plant operations, process efficiency, mineral recovery optimization, safety scenarios, environmental management, troubleshooting, and integrated mineral processing challenges.',
    benefit:
      'Students analyze facts, separate known information from assumptions, identify missing evidence, and develop justified technical solutions.',
    value: 'USD 14',
    icon: FileTextIcon,
  },
  {
    number: '04',
    title: 'Mineral Processing Teacher Dictionary: 200 Essential Concepts Explained',
    description:
      'Comprehensive reference handbook covering 200 essential mineral processing concepts designed to help teachers explain vocabulary and concepts clearly. Includes terminology related to:',
    items: [
      'Mineral Processing Fundamentals',
      'Comminution and Size Reduction',
      'Screening and Classification',
      'Gravity Separation Methods',
      'Magnetic Separation',
      'Froth Flotation',
      'Reagents and Chemistry',
      'Dewatering and Filtration',
      'Plant Operations',
      'Process Monitoring',
      'Equipment Performance',
      'Troubleshooting',
      'Safety and Risk',
      'Environmental Management',
      'Sustainability',
      'Technical Communication',
      'Process Design',
      'Optimization and Efficiency',
      'Recovery Concepts',
      'Equipment and Systems',
    ],
    benefit: 'Project it in class and your students instantly understand complex mineral processing concepts.',
    value: 'USD 14',
    icon: BookOpenCheckIcon,
  },
  {
    number: '05',
    title: 'Mineral Processing Teacher Lesson Planning Guide',
    description:
      'A complete planning resource designed to help educators turn technical material into organized lessons. Includes 30 ready-to-use lesson structures.',
    groups: [
      {
        heading: 'Organized into six teaching blocks',
        items: [
          'BLOCK 1 — Fundamentals & Comminution',
          'BLOCK 2 — Classification & Separation Methods',
          'BLOCK 3 — Flotation & Concentration',
          'BLOCK 4 — Dewatering & Plant Systems',
          'BLOCK 5 — Safety, Environment & Sustainability',
          'BLOCK 6 — Operations & Engineering Reasoning',
        ],
      },
      {
        heading: 'Each lesson structure includes',
        items: [
          'Learning Objectives',
          'Key Concepts',
          'Lesson Opening',
          'Core Explanation',
          'Guided Classroom Task',
          'Student Questions',
          'Understanding Check',
          'Common Misconceptions',
          'Lesson Connections',
        ],
      },
    ],
    cycle: ['Plan', 'Introduce', 'Explain', 'Connect', 'Apply', 'Question', 'Assess', 'Review', 'Integrate'],
    benefit: 'The guide follows a structured teaching cycle so every lesson is organized from start to finish.',
    value: 'USD 14',
    icon: CalendarCheckIcon,
    wide: true,
  },
];
